// Подготовка данных для TimeSeriesChart: отбор по периоду, сведение серий по времени, шкалы осей.
// Чистые функции — их проверяют тесты, компонент только рисует результат.

import { periodRange, type Period } from '@/lib/periods'
import { formatNumber, toDate } from '@/lib/format'

export type SeriesPoint = {
  /** 'YYYY-MM-DD' (местная полночь) или ISO-время. */
  x: string
  y: number | null
}

export type ChartSeries = {
  key: string
  label: string
  points: SeriesPoint[]
  unit?: string
  /** Ось; по умолчанию вторая серия с другой единицей уходит направо. */
  axis?: 'left' | 'right'
  /** Знаков после запятой в подсказке (по умолчанию 1). */
  digits?: number
}

export type ChartGoal = {
  /** Постоянная цель — горизонтальная линия. */
  value?: number
  /** Цель, менявшаяся со временем: с даты x действует y до следующей точки — линия ступенькой. */
  steps?: SeriesPoint[]
  label?: string
  axis?: 'left' | 'right'
}

/** Ключ строки графика со значением ступенчатой цели. */
export const GOAL_KEY = '__goal'

/** Значение ступенчатой цели на момент x: последняя ступень не позже x; до первой — null. */
export function stepValueAt(steps: SeriesPoint[], x: string | number): number | null {
  const time = typeof x === 'number' ? x : pointTime(x)
  let value: number | null = null
  let at = Number.NEGATIVE_INFINITY
  for (const s of steps) {
    const t = pointTime(s.x)
    if (t <= time && t >= at && s.y !== null && Number.isFinite(s.y)) {
      value = s.y
      at = t
    }
  }
  return value
}

/** Коридор нормы: заливка между low и high. */
export type ChartBand = {
  low: number
  high: number
  label?: string
  axis?: 'left' | 'right'
}

export type ChartRow = { t: number } & Record<string, number | null>

export type PreparedSeries = ChartSeries & { axis: 'left' | 'right' }

export type AxisScale = { domain: [number, number]; keys: string[]; unit?: string }

export type PreparedChart = {
  rows: ChartRow[]
  /** Есть ступенчатая цель — у строк заполнен `GOAL_KEY`. */
  steppedGoal: boolean
  series: PreparedSeries[]
  axes: { left: AxisScale; right: AxisScale | null }
  xDomain: [number, number]
  hasData: boolean
}

export function pointTime(x: string): number {
  return toDate(x)?.getTime() ?? Number.NaN
}

function assignAxes(series: ChartSeries[]): PreparedSeries[] {
  const firstUnit = series[0]?.unit
  return series.map((s, index) => ({
    ...s,
    axis: s.axis ?? (index > 0 && s.unit !== firstUnit ? 'right' : 'left'),
  }))
}

/** Шкала с запасом 8 % сверху и снизу; одно значение — ±1. */
/** Деления оси времени равномерно по всему периоду (а не только там, где есть точки). */
export function axisTicks([start, end]: [number, number], count = 4): number[] {
  if (!(end > start) || count < 2) return [start]
  const step = (end - start) / (count - 1)
  return Array.from({ length: count }, (_, i) => Math.round(start + step * i))
}

/** Подписи оси Y: при размахе от 100 — целыми (шаги, калории), иначе с одним знаком; ширина оси —
 * по самой длинной подписи, чтобы пятизначные числа не обрезались. */
export function yAxisFormat([min, max]: [number, number], minWidth: number) {
  const digits = max - min >= 100 ? 0 : 1
  const format = (v: number) => formatNumber(v, { digits })
  const longest = Math.max(format(min).length, format(max).length)
  return { format, width: Math.max(minWidth, Math.ceil(longest * 6.5) + 8) }
}

export function paddedDomain(values: number[]): [number, number] {
  if (values.length === 0) return [0, 1]
  let min = Math.min(...values)
  let max = Math.max(...values)
  if (min === max) {
    const pad = Math.max(1, Math.abs(min) * 0.02)
    return [min - pad, max + pad]
  }
  const pad = (max - min) * 0.08
  min -= pad
  max += pad
  return [min, max]
}

export function prepareChartData(input: {
  series: ChartSeries[]
  period: Period
  today: string
  goal?: ChartGoal | null
  band?: ChartBand | null
}): PreparedChart {
  const { period, today, goal, band } = input
  const series = assignAxes(input.series)
  const range = periodRange(period, today)
  const from = range.start === null ? Number.NEGATIVE_INFINITY : pointTime(range.start)
  const endDay = pointTime(range.end)
  const until = endDay + 24 * 3600 * 1000

  const byTime = new Map<number, ChartRow>()
  for (const s of series) {
    for (const p of s.points) {
      const time = pointTime(p.x)
      if (!Number.isFinite(time) || time < from || time >= until) continue
      let row = byTime.get(time)
      if (!row) {
        row = { t: time } as ChartRow
        for (const other of series) row[other.key] = null
        byTime.set(time, row)
      }
      row[s.key] = p.y === null || !Number.isFinite(p.y) ? null : p.y
    }
  }
  const hasData = [...byTime.values()].some((row) => series.some((s) => typeof row[s.key] === 'number'))
  const times = [...byTime.keys()]
  const firstT = times.length ? Math.min(...times) : endDay
  const lastT = times.length ? Math.max(...times) : endDay
  const xStart = range.start === null ? firstT : from
  const xEnd = Math.max(endDay, lastT)

  // Ступенчатая цель: строки на краях периода и в дни смены, значение цели — в каждой строке.
  const steps = goal?.steps?.filter((p) => p.y !== null && Number.isFinite(pointTime(p.x))) ?? []
  const steppedGoal = steps.length > 0 && hasData
  if (steppedGoal) {
    const edges = [xStart, xEnd, ...steps.map((p) => pointTime(p.x)).filter((t) => t > xStart && t < xEnd)]
    for (const time of edges) {
      if (byTime.has(time)) continue
      const row = { t: time } as ChartRow
      for (const other of series) row[other.key] = null
      byTime.set(time, row)
    }
    for (const row of byTime.values()) row[GOAL_KEY] = stepValueAt(steps, row.t)
  }
  const rows = [...byTime.values()].sort((a, b) => a.t - b.t)

  const scale = (axis: 'left' | 'right'): AxisScale | null => {
    const members = series.filter((s) => s.axis === axis)
    if (members.length === 0) return null
    const values: number[] = []
    for (const row of rows) {
      for (const s of members) {
        const v = row[s.key]
        if (typeof v === 'number') values.push(v)
      }
    }
    if (goal && (goal.axis ?? 'left') === axis && values.length > 0) {
      if (goal.value !== undefined) values.push(goal.value)
      if (steppedGoal) {
        for (const row of rows) {
          const v = row[GOAL_KEY]
          if (typeof v === 'number') values.push(v)
        }
      }
    }
    if (band && (band.axis ?? 'left') === axis && values.length > 0) values.push(band.low, band.high)
    return { domain: paddedDomain(values), keys: members.map((s) => s.key), unit: members[0]?.unit }
  }

  return {
    rows,
    steppedGoal,
    series,
    axes: { left: scale('left') ?? { domain: [0, 1], keys: [] }, right: scale('right') },
    xDomain: [Math.min(xStart, xEnd), xEnd],
    hasData,
  }
}
